import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Drives the "Existing customer / New customer" choice on the contract form.
// The inactive side lives in a disabled <fieldset> so it neither submits nor
// validates — which also keeps the live-pricing preview (contract-form) honest,
// since it posts a FormData that omits disabled inputs.
export default class extends Controller {
  static targets = [
    "existingFieldset",
    "newFieldset",
    "personFieldset",
    "businessFieldset",
    "select",
    "duplicateWarning",
  ]

  declare readonly existingFieldsetTarget: HTMLFieldSetElement
  declare readonly newFieldsetTarget: HTMLFieldSetElement
  declare readonly hasPersonFieldsetTarget: boolean
  declare readonly personFieldsetTarget: HTMLFieldSetElement
  declare readonly hasBusinessFieldsetTarget: boolean
  declare readonly businessFieldsetTarget: HTMLFieldSetElement
  declare readonly hasSelectTarget: boolean
  declare readonly selectTarget: HTMLSelectElement
  declare readonly hasDuplicateWarningTarget: boolean
  declare readonly duplicateWarningTarget: HTMLElement

  private debounceTimer?: ReturnType<typeof setTimeout>

  connect() {
    const mode = this.checkedValue('customer_mode') || "existing"
    this.setMode(mode)
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  toggleMode = (e: Event) => {
    this.setMode((e.target as HTMLInputElement).value)
  }

  changeType = () => {
    this.applyType()
  }

  checkDuplicates = (e: Event) => {
    const name = (e.target as HTMLElement)?.getAttribute?.("name") || ""
    if (!/\[(name|first_name|last_name|email|number)\]/.test(name)) return

    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.runDuplicateCheck(), 350)
  }

  useExisting = (e: Event) => {
    e.preventDefault()
    const id = (e.currentTarget as HTMLElement).dataset.partyId
    if (this.hasSelectTarget && id) {
      // The select is enhanced by the tom-select controller; drive its instance
      // API so the visible combobox updates, falling back to the raw select.
      const tomselect = (this.selectTarget as unknown as { tomselect?: { setValue(v: string): void } }).tomselect
      if (tomselect) {
        tomselect.setValue(id)
      } else {
        this.selectTarget.value = id
        this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
    this.setMode("existing")
    this.clearWarning()
  }

  private setMode(mode: string) {
    const existing = mode !== "new"

    // Disabled keeps the inactive side out of the form submit and validation;
    // hidden keeps it off the screen. Both matter.
    this.existingFieldsetTarget.disabled = !existing
    this.existingFieldsetTarget.classList.toggle("hidden", !existing)
    this.newFieldsetTarget.disabled = existing
    this.newFieldsetTarget.classList.toggle("hidden", existing)

    const radio = this.element.querySelector<HTMLInputElement>(`#customer-mode-${mode}`)
    if (radio) radio.checked = true

    if (existing) {
      this.clearWarning()
    } else {
      this.applyType()
    }
  }

  private applyType() {
    const type = this.checkedValue("contract[party_attributes][partyable_type]") || "Business"
    if (this.hasPersonFieldsetTarget) {
      this.personFieldsetTarget.disabled = type !== "Person"
      this.personFieldsetTarget.classList.toggle("hidden", type !== "Person")
    }
    if (this.hasBusinessFieldsetTarget) {
      this.businessFieldsetTarget.disabled = type !== "Business"
      this.businessFieldsetTarget.classList.toggle("hidden", type !== "Business")
    }
  }

  private runDuplicateCheck() {
    const params = this.buildParams()
    if (!params) {
      this.clearWarning()
      return
    }
    get(`/parties/search?${params.toString()}`, { responseKind: "turbo-stream" })
  }

  // Send the identifying fields as structured params so the server can match each
  // precisely (near-exact name, exact email, exact phone) rather than fuzzing one
  // combined string. Returns null when there isn't enough signal yet.
  private buildParams(): URLSearchParams | null {
    const fieldset = this.activePartyableFieldset()
    if (!fieldset) return null

    const type = this.checkedValue("contract[party_attributes][partyable_type]")
    if (!type) return null

    const value = (selector: string) =>
      (fieldset.querySelector<HTMLInputElement>(selector)?.value || "").trim()

    const first = value('input[name*="[first_name]"]')
    const last = value('input[name*="[last_name]"]')
    const name = type === "Business" ? value('input[name*="[name]"]') : `${first} ${last}`.trim()
    const email = value('input[name*="[email]"]')
    const phone = value('input[name*="[number]"]')

    const hasName = name.length >= 3
    const hasEmail = /.+@.+\..+/.test(email)
    const hasPhone = phone.replace(/\D/g, "").length >= 7
    if (!hasName && !hasEmail && !hasPhone) return null

    const params = new URLSearchParams({ type })
    if (hasName) params.set("name", name)
    if (hasEmail) params.set("email", email)
    if (hasPhone) params.set("phone", phone)
    return params
  }

  private activePartyableFieldset(): HTMLFieldSetElement | null {
    if (this.hasPersonFieldsetTarget && !this.personFieldsetTarget.disabled) return this.personFieldsetTarget
    if (this.hasBusinessFieldsetTarget && !this.businessFieldsetTarget.disabled) return this.businessFieldsetTarget
    return null
  }

  private clearWarning() {
    if (this.hasDuplicateWarningTarget) this.duplicateWarningTarget.innerHTML = ""
  }

  private checkedValue(name: string): string | null {
    const escaped = name.replace(/(["\\])/g, "\\$1")
    const el = this.element.querySelector<HTMLInputElement>(`input[name="${escaped}"]:checked`)
    return el ? el.value : null
  }
}
